import React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import Heart from '../../../../../static/heart.svg';
import HeartFull from '../../../../../static/heartFull.svg';
import * as styles from './cardOt.module.scss';
import { CardOtQuery, HomePageQuery } from '../../../../../graphql-types';
import { LocaleLink } from '../../localeLink/LocaleLink';
import { useWishlistContext } from '../../../../hooks/contexts/useWishlistContext';
import { Path, usePageContext } from '../../../../hooks/contexts/usePageContext';
import { sanitizeSlug } from '../../../../utils/sanitizeSlug';
import { URLS } from '../../../../../constants';
import { WidgetReviews } from '../../../../modules/ingenie/WidgetReviews';

const IMG_PLACEHOLDER =
  'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png';
interface ICardOtProps {
  ot: HomePageQuery['allIngenieOt']['nodes'][0];
  rubriquePath?: Path;
  agenda?: boolean;
}

export const isLiked = (e, ot, modifyWishlist) => {
  e.preventDefault();
  modifyWishlist(ot);
};

export const CardOt = ({ ot, rubriquePath, agenda = false }: ICardOtProps) => {
  const { lang } = usePageContext();
  const { wishlist, modifyWishlist } = useWishlistContext();
  const { sanityTranslations } = useStaticQuery<CardOtQuery>(QUERY);
  const usableCodeForIngenieWidget = ot?.codePrestation ?? ot?.codePrestataire;
  const liked = !!wishlist.find((x) => x.id === ot?.id);
  const parentUrl = rubriquePath ? rubriquePath.url : ot?.parentRubriques?.[0]?.slug;
  const tarifsFrom = ot?.tarifs
    ?.filter((x) => x?.libelle?.fr === 'Chambre double')
    ?.map((x) => parseInt(x?.prix_min || '0', 10));
  const priceFrom =
    tarifsFrom?.length !== 0 && tarifsFrom?.[0] && tarifsFrom?.[0] > 0 ? tarifsFrom[0] : null;

  const image = ot.medias?.[0]?.resolutions?.find((resolution) => resolution?.type === '320x240');
  return (
    <LocaleLink
      className={styles.wrapper}
      to={
        agenda
          ? `${URLS.agenda.url}/${sanitizeSlug(ot?.libelle?.fr)}`
          : `${URLS['preparez-votre-sejour'].url}/${parentUrl}/${sanitizeSlug(ot?.libelle?.fr)}`
      }
    >
      <div className={styles.hideOverflow}>
        <div className={styles.imageWrapper}>
          <img
            alt="tours-tourisme"
            loading="lazy"
            src={image?.url ?? ''}
            onError={(e) => {
              e.currentTarget.src = IMG_PLACEHOLDER;
            }}
          />
          <div>
            {ot?.pictosPartners?.map((picto, index) => (
              <img
                width={50}
                height={50}
                key={picto?.url ?? index}
                src={picto?.url ?? ''}
                alt={picto?.alt ?? 'logo placeholder'}
              />
            ))}
          </div>
          <button
            aria-label="Like"
            type="button"
            onClick={(e) => isLiked(e, ot, modifyWishlist)}
            className={styles.likeWrapper}
          >
            {liked ? <HeartFull className={styles.heart} /> : <Heart className={styles.heart} />}
          </button>
          {priceFrom && (
            <div className={styles.priceFrom}>
              <p>
                {sanityTranslations?._rawLabelCardBeforePrice?.[lang]}
                <span
                  className={styles.price}
                >{`${priceFrom}/${sanityTranslations?._rawLabelCardAfterPrice?.[lang]}.`}</span>
              </p>
            </div>
          )}
        </div>
        <div className={styles.content}>
          <div className={styles.title}>{ot?.libelle?.[lang] ?? ''}</div>
          <p
            className={styles.description}
            dangerouslySetInnerHTML={{
              __html: ot?.descriptifs?.[0]?.contenu?.[lang]?.replace(/<\/?[^>]+(>|$)/g, ''),
            }}
          />
          {(ot?.reservable || ot?.onlyReservableOnIngenie) && (
            <div className={styles.link}>
              {ot?.reservable && usableCodeForIngenieWidget && (
                <WidgetReviews codePrestation={usableCodeForIngenieWidget} onlyResume />
              )}
              <button type="button" className={styles.buttonLink}>
                {sanityTranslations?._rawLinkLabel?.[lang]}
              </button>
            </div>
          )}
        </div>
      </div>
    </LocaleLink>
  );
};

const QUERY = graphql`
  query CardOt {
    sanityTranslations {
      _rawLinkLabel
      _rawLabelCardBeforePrice
      _rawLabelCardAfterPrice
    }
  }
`;
